import { AxiosError, isAxiosError } from "axios";
import { z } from "zod";
import {
    ScheduleLinkedSourceConflictErrorBody,
    ScheduleSourceMoveConflictErrorBody,
    scheduleLinkedSourceConflictErrorSchema,
    scheduleSourceMoveConflictErrorSchema,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ReconciliationErrorCodes } from "@common/contracts/reconciliation.contract";
import { Lock } from "@common/types/lock";
import {
    addCalendarDays,
    getUTCDateForPST,
    toUTCDateOnly,
} from "@common/helpers/date";
import { isScheduleLifecyclePeriodLockedError } from "@common/helpers/domains/accounting/scheduleLifecycleErrors";
import {
    ScheduleLifecyclePeriodLockedError,
    scheduleConflictResponseSchema,
} from "@common/schemas/domains/accounting/scheduleLifecycle.schema";

export function getLockDate(locks?: Array<Lock | undefined>): Date | null {
    return locks?.[0]?.date ? getUTCDateForPST(locks?.[0]?.date) : null;
}

/**
 * The most restrictive (latest) lock date among a set of locks, e.g. the
 * distinct entities behind a bulk selection. Locks are single cut-off dates,
 * so the locked range for each entity is a prefix ending at its own lock
 * date — the union of those prefixes ends at the latest one. Any date after
 * it is guaranteed unlocked for every entity in the set.
 */
export function getLatestLockDate(locks: Lock[]): Date | null {
    return locks.reduce<Date | null>((latest, lock) => {
        if (!lock.date) {
            return latest;
        }
        const lockDate = getUTCDateForPST(lock.date);
        return !latest || lockDate > latest ? lockDate : latest;
    }, null);
}

/**
 * The later of two optional date-only (YYYY-MM-DD) minimums — used to combine
 * independent "may not precede X" rules (invoice date, period lock) into a
 * single DatePicker `minDate`. Date-only strings sort lexicographically like
 * calendar days (see `isDateOnlyBefore`), so this is a plain string compare.
 */
export function laterDateOnly(
    first: string | undefined,
    second: string | undefined,
): string | undefined {
    if (!first) {
        return second;
    }
    if (!second) {
        return first;
    }
    return first > second ? first : second;
}

export function clampToMinDateOnly(
    dateOnly: string,
    minDateOnly: string | undefined,
): string {
    return laterDateOnly(dateOnly, minDateOnly) ?? dateOnly;
}

export function isLockedByDateOnly(
    lockDate: Date | null,
    dateOnly?: string | null,
): boolean {
    if (!lockDate || !dateOnly) {
        return false;
    }

    return dateOnly <= toUTCDateOnly(lockDate);
}

export function isReconciliationPeriodLockedError(error: unknown): boolean {
    return (
        isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response.data?.errorCode ===
            ReconciliationErrorCodes.PERIOD_LOCKED
    );
}

/**
 * The structured body of a schedule 409 refused for locked periods, or null
 * for any other error. Parsed rather than duck-typed so a body that only
 * looks right (a plain conflict `message`) is not mistaken for one.
 */
export function getScheduleLifecyclePeriodLockedError(
    error: unknown,
): ScheduleLifecyclePeriodLockedError | null {
    if (!isAxiosError(error)) {
        return null;
    }
    const body = parseScheduleConflictBody(
        error,
        scheduleConflictResponseSchema,
    );
    return body && isScheduleLifecyclePeriodLockedError(body) ? body : null;
}

/**
 * The structured body of a 409 refused because a payment and the invoice it
 * pays would both carry a live schedule, or null for any other error.
 */
export function getScheduleLinkedSourceConflict(
    error: unknown,
): ScheduleLinkedSourceConflictErrorBody | null {
    if (!isAxiosError(error)) {
        return null;
    }
    return parseScheduleConflictBody(
        error,
        scheduleLinkedSourceConflictErrorSchema,
    );
}

/**
 * The structured body of a 409 that offers to move a payment's schedule onto
 * the bill or invoice, or null for any other error.
 */
export function getScheduleSourceMoveConflict(
    error: unknown,
): ScheduleSourceMoveConflictErrorBody | null {
    if (!isAxiosError(error)) {
        return null;
    }
    return parseScheduleConflictBody(
        error,
        scheduleSourceMoveConflictErrorSchema,
    );
}

export function parseScheduleConflictBody<TBody>(
    error: AxiosError,
    schema: z.ZodType<TBody>,
): TBody | null {
    if (error.response?.status !== 409) {
        return null;
    }
    const parsed = schema.safeParse(error.response.data);
    return parsed.success ? parsed.data : null;
}

/** First day a date picker may offer once books are locked through `lockDate`. */
export function getFirstUnlockedDateOnly(
    lockDate: Date | null,
): string | undefined {
    return lockDate ? addCalendarDays(toUTCDateOnly(lockDate), 1) : undefined;
}
